'use client';

import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useIntersectionObserver } from './useIntersectionObserver';
import type { QueryKey } from '@tanstack/react-query';
import type { Pagination } from '../data-table/types';
export interface UseInfiniteScrollPaginationConfig<TData, TRow, TParams extends Record<string, unknown> = Record<string, unknown>> {
  /** Query key for React Query */
  queryKey: QueryKey;
  /** Fetcher function that accepts query params and returns data */
  fetcher: (params: TParams) => Promise<TData>;
  /**
   * Builds the full query params for each page. Called with `undefined` for the
   * first page and the cursor string for subsequent pages. This ensures cursor
   * values are mapped through the same param-building logic as the first page
   * (e.g., `nextCursor` → `pagination_token`).
   */
  getPageParams: (cursor: string | undefined) => TParams;
  /** Extract data array from response */
  getDataFromResponse: (response: TData) => TRow[];
  /** Extract pagination info from response */
  getPaginationFromResponse: (response: TData) => Pagination;
  /** Interval in ms to refetch data (e.g. 30_000 for 30s polling) */
  refetchInterval?: number;
  /** Duration in ms before fetched data becomes stale */
  staleTime?: number;
  /** React Query retry behavior */
  retry?: boolean | number | ((failureCount: number, error: Error) => boolean);
  /** Whether the query is enabled (default: true) */
  enabled?: boolean;
  /** Continue through empty pages when the server returns a next cursor (default: false). */
  continueOnEmptyPages?: boolean;
  /**
   * Root element for intersection detection.
   * - `null` (default): uses the viewport
   * - `'nearest'`: auto-detects the nearest scrollable ancestor of the sentinel element
   * - `RefObject`: uses the element referenced by the ref
   */
  scrollContainerRef?: React.RefObject<Element | null> | 'nearest' | null;
}
export interface UseInfiniteScrollPaginationResult<TRow, TElement extends HTMLElement> {
  /** Flattened data from all loaded pages */
  data: TRow[];
  /** Total count across all pages */
  totalCount?: number;
  /** Whether there are more pages to load */
  hasNextPage: boolean;
  /** Whether the next page is currently being fetched */
  isFetchingNextPage: boolean;
  /** Error from fetching a subsequent page, while previously loaded data remains available */
  nextPageError: Error | null;
  /** Explicitly retries the failed subsequent page */
  retryNextPage: () => void;
  /** Ref to attach to sentinel element for auto-loading */
  sentinelRef: React.RefObject<TElement | null>;
  /** Whether the initial query is still loading */
  isLoading: boolean;
  /** Any error from the query */
  error: Error | null;
}

/**
 * Generic hook for infinite scroll pagination that accumulates all pages in memory.
 * Uses React Query's useInfiniteQuery with cursor-based pagination.
 *
 * This is the shared implementation - apps should create thin wrappers that inject
 * their app-specific fetcher function (e.g., callServerAction with auth handling).
 */
export function useInfiniteScrollPagination<TData, TRow, TElement extends HTMLElement = HTMLDivElement, TParams extends Record<string, unknown> = Record<string, unknown>>(config: UseInfiniteScrollPaginationConfig<TData, TRow, TParams>): UseInfiniteScrollPaginationResult<TRow, TElement> {
  const {
    queryKey,
    fetcher,
    getPageParams,
    getDataFromResponse,
    getPaginationFromResponse,
    refetchInterval,
    staleTime,
    retry,
    enabled,
    continueOnEmptyPages = false,
    scrollContainerRef
  } = config;
  const {
    data: queryData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    isLoading,
    error
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({
      pageParam
    }) => {
      return fetcher(getPageParams(pageParam));
    },
    getNextPageParam: lastPage => {
      if (!lastPage) return undefined;
      const data = getDataFromResponse(lastPage);
      // Empty pages stop by default; opt-in callers may continue when the server returns a cursor.
      if (data.length === 0 && !continueOnEmptyPages) return undefined;
      const pagination = getPaginationFromResponse(lastPage);
      return pagination.cursors?.nextCursor ?? undefined;
    },
    initialPageParam: undefined as string | undefined,
    enabled,
    staleTime,
    retry,
    ...(refetchInterval && {
      refetchInterval
    })
  });

  // Flatten all pages into a single data array
  const flattenedData = useMemo(() => {
    if (!queryData?.pages) return [];
    return queryData.pages.flatMap(page => getDataFromResponse(page));
  }, [queryData?.pages, getDataFromResponse]);

  // Get total count from the last page (most up-to-date)
  const totalCount = useMemo(() => {
    if (!queryData?.pages || queryData.pages.length === 0) return undefined;
    const lastPage_0 = queryData.pages[queryData.pages.length - 1];
    const pagination_0 = getPaginationFromResponse(lastPage_0);
    return pagination_0.totalCount;
  }, [queryData?.pages, getPaginationFromResponse]);

  // Setup intersection observer to auto-load next page
  const sentinelRef = useIntersectionObserver<TElement>({
    onIntersect: () => {
      if (hasNextPage && !isFetchingNextPage && !isFetchNextPageError) {
        fetchNextPage();
      }
    },
    enabled: !!hasNextPage && !isFetchingNextPage && !isFetchNextPageError,
    rootMargin: '100px',
    root: scrollContainerRef
  });
  function retryNextPage() {
    void fetchNextPage();
  }
  return {
    data: flattenedData,
    totalCount,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    nextPageError: isFetchNextPageError ? error : null,
    retryNextPage,
    sentinelRef: sentinelRef as React.RefObject<TElement | null>,
    isLoading,
    error: error as Error | null
  };
}